import React, { useEffect, useState, useRef } from 'react';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import { SharjahPoliceEmblem } from '../SharjahPoliceEmblem';
import { ZapIcon, UserCheckIcon, FileTextIcon, HistoryIcon, LinkIcon, ReceiptIcon, ShieldCheckIcon, AlertTriangleIcon, ScanFaceIcon, MaximizeIcon, MinimizeIcon } from 'lucide-react';
const PAIR2_LOGO = 'https://cdn.magicpatterns.com/uploads/51QRCG1WJpxN6EwND9yhQF/Pair2_(2).svg';
const BULLETS = [{
  icon: ZapIcon,
  text: 'عرض فوري لبيانات السائق'
}, {
  icon: UserCheckIcon,
  text: 'ملف متكامل للسائق والمركبة'
}, {
  icon: FileTextIcon,
  text: 'عرض المستندات الرسمية'
}, {
  icon: HistoryIcon,
  text: 'سجل كامل للحركة والتاريخ'
}, {
  icon: LinkIcon,
  text: 'ربط مباشر بالمخالفات'
}];
export function SolutionSlide() {
  const reduce = useReducedMotion();
  const base = reduce ? {
    duration: 0
  } : undefined;
  return <section dir="rtl" className="relative w-screen h-screen overflow-hidden flex flex-col" style={{
    background: '#EDF1F4'
  }} data-id="element-565">
      {/* ===== BACKGROUND ===== */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden data-id="element-566">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[50vmax] h-[50vmax] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, rgba(47,169,156,0.035) 0%, transparent 55%)'
      }} data-id="element-567" />
        <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='48' height='48' viewBox='0 0 48 48' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h48v48H0V0zm1 1h46v46H1V1z' fill='%230B1328' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`
      }} data-id="element-568" />
      </div>

      {/* ===== TOP BAR ===== */}
      <motion.div initial={{
      opacity: 0,
      y: -14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={base ?? {
      duration: 0.8,
      delay: 0.1,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative z-20 flex items-center justify-between pt-[clamp(1rem,2.5vh,2rem)] px-[clamp(1.5rem,4vw,4rem)]" data-id="element-569">
        <SharjahPoliceEmblem variant="light" data-id="element-570" />
        <img src={PAIR2_LOGO} alt="Pair²" className="h-[clamp(2.25rem,4.5vh,3.25rem)] w-auto" style={{
        filter: 'drop-shadow(0 4px 18px rgba(47,169,156,0.28))'
      }} draggable={false} data-id="element-571" />
      </motion.div>

      {/* ===== TITLE ===== */}
      <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={base ?? {
      duration: 0.8,
      delay: 0.2
    }} className="relative z-10 px-[clamp(2rem,5vw,5rem)] mt-[clamp(0.5rem,1.5vh,1rem)]" data-id="element-572">
        <h2 className="font-arabic-display font-black text-right leading-[1.15]" style={{
        color: '#0B1328',
        fontSize: 'clamp(1.3rem, 2.2vw, 2rem)'
      }} data-id="element-573">
          مع{' '}
          <span style={{
          color: '#2FA99C'
        }} data-id="element-574">
            Pair²
          </span>{' '}
          — هكذا تتم العملية
        </h2>
      </motion.div>

      {/* ===== MAIN CONTENT ===== */}
      <div className="relative z-10 flex-1 min-h-0 grid px-[clamp(1.5rem,4vw,4rem)] pt-[clamp(0.75rem,2vh,1.5rem)] pb-0" style={{
      gridTemplateColumns: '1fr 1.85fr',
      gap: 'clamp(1.25rem, 2.5vw, 2.5rem)'
    }} data-id="element-575">
        {/* RIGHT COLUMN (first in RTL) — Supporting Content */}
        <motion.div initial={{
        opacity: 0,
        x: -14
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={base ?? {
        duration: 0.8,
        delay: 0.35
      }} className="flex flex-col justify-start pt-[clamp(0.5rem,1vh,0.75rem)] min-w-0" data-id="element-576">
          {/* Top statement */}
          <p className="font-arabic-body font-medium text-right leading-[1.9] mb-[clamp(1rem,2.5vh,1.75rem)]" style={{
          color: '#1A1F2B',
          fontSize: 'clamp(0.82rem, 1vw, 0.95rem)'
        }} data-id="element-577">
            بمجرد البحث برقم المركبة، يتم عرض بيانات السائق المرتبط بها بشكل
            فوري
          </p>

          {/* Bullet points */}
          <div className="flex flex-col gap-[clamp(0.6rem,1.5vh,1rem)] mb-[clamp(1rem,2.5vh,1.75rem)]" data-id="element-578">
            {BULLETS.map((b, i) => {
            const Icon = b.icon;
            return <motion.div key={i} initial={{
              opacity: 0,
              x: -8
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={base ?? {
              duration: 0.45,
              delay: 0.5 + i * 0.08
            }} className="flex items-center gap-[clamp(0.4rem,0.6vw,0.55rem)]" dir="rtl" data-id="element-579">
                  <div className="shrink-0 rounded-full flex items-center justify-center" style={{
                width: 'clamp(1.6rem, 2.2vw, 2rem)',
                height: 'clamp(1.6rem, 2.2vw, 2rem)',
                background: 'rgba(47,169,156,0.08)',
                border: '1px solid rgba(47,169,156,0.18)'
              }} data-id="element-580">
                    <Icon style={{
                  width: 'clamp(0.75rem, 0.92vw, 0.88rem)',
                  height: 'clamp(0.75rem, 0.92vw, 0.88rem)',
                  color: '#2FA99C'
                }} strokeWidth={1.8} data-id="element-581" />
                  </div>
                  <span className="font-arabic-body font-semibold" style={{
                color: '#1A1F2B',
                fontSize: 'clamp(0.78rem, 0.92vw, 0.88rem)'
              }} data-id="element-582">
                    {b.text}
                  </span>
                </motion.div>;
          })}
          </div>

          {/* Two small highlight cards */}
          <div className="flex flex-col gap-[clamp(0.5rem,1.2vh,0.85rem)]" data-id="element-583">
            {/* أمنياً */}
            <motion.div initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={base ?? {
            duration: 0.5,
            delay: 1.0
          }} className="rounded-lg p-[clamp(0.6rem,1.2vh,0.85rem)]" style={{
            background: 'rgba(255,255,255,0.8)',
            border: '1px solid rgba(47,169,156,0.12)',
            boxShadow: '0 3px 12px -3px rgba(14,93,85,0.05)'
          }} data-id="element-584">
              <div className="flex items-center gap-2 mb-1" dir="rtl" data-id="element-585">
                <ShieldCheckIcon style={{
                width: '0.85rem',
                height: '0.85rem',
                color: '#2FA99C'
              }} strokeWidth={2} data-id="element-586" />
                <span className="font-arabic-display font-bold text-[#2FA99C]" style={{
                fontSize: 'clamp(0.78rem, 0.95vw, 0.9rem)'
              }} data-id="element-587">
                  أمنياً
                </span>
              </div>
              <p className="font-arabic-body text-right" style={{
              color: '#1A1F2B',
              fontSize: 'clamp(0.7rem, 0.85vw, 0.8rem)',
              opacity: 0.8,
              lineHeight: 1.7
            }} data-id="element-588">
                تحديد هوية السائق فورياً مع عرض المستندات الرسمية
              </p>
            </motion.div>

            {/* مرورياً */}
            <motion.div initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={base ?? {
            duration: 0.5,
            delay: 1.1
          }} className="rounded-lg p-[clamp(0.6rem,1.2vh,0.85rem)]" style={{
            background: 'rgba(255,255,255,0.8)',
            border: '1px solid rgba(47,169,156,0.12)',
            boxShadow: '0 3px 12px -3px rgba(14,93,85,0.05)'
          }} data-id="element-589">
              <div className="flex items-center gap-2 mb-1" dir="rtl" data-id="element-590">
                <ReceiptIcon style={{
                width: '0.85rem',
                height: '0.85rem',
                color: '#2FA99C'
              }} strokeWidth={2} data-id="element-591" />
                <span className="font-arabic-display font-bold text-[#2FA99C]" style={{
                fontSize: 'clamp(0.78rem, 0.95vw, 0.9rem)'
              }} data-id="element-592">
                  مرورياً
                </span>
              </div>
              <p className="font-arabic-body text-right" style={{
              color: '#1A1F2B',
              fontSize: 'clamp(0.7rem, 0.85vw, 0.8rem)',
              opacity: 0.8,
              lineHeight: 1.7
            }} data-id="element-593">
                ربط المخالفات المرورية بالسائق الفعلي بشكل مباشر
              </p>
            </motion.div>
          </div>
        </motion.div>

        {/* LEFT COLUMN (second in RTL) — Video Container */}
        <VideoContainer base={base} data-id="element-594" />
      </div>

      {/* ===== BOTTOM STATEMENT ===== */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={base ?? {
      duration: 0.8,
      delay: 1.4
    }} className="relative z-20 pb-[clamp(2rem,4vh,3rem)] pt-[clamp(0.75rem,1.5vh,1rem)] flex justify-center" data-id="element-595">
        <p className="font-arabic-body font-bold text-center" style={{
        color: '#0B1328',
        fontSize: 'clamp(0.9rem, 1.1vw, 1.05rem)'
      }} data-id="element-596">
          الوصول إلى المعلومة خلال{' '}
          <span style={{
          color: '#2FA99C'
        }} data-id="element-597">
            ثوانٍ
          </span>{' '}
          بدلاً من سلسلة إجراءات معقدة
        </p>
      </motion.div>
    </section>;
}
const PLAYLIST = ['https://cdn.magicpatterns.com/uploads/eP1mrccixHxEX9g8ynoBpQ/Pair2GIF1-ezgif.com-gif-to-mp4-converter.mp4', 'https://cdn.magicpatterns.com/uploads/7k4cFDbyUPCozp2KKsVjg4/TrackingGIF2-ezgif.com-gif-to-mp4-converter.mp4'];
function VideoContainer({
  base
}: {
  base?: {
    duration: number;
  };
}) {
  const [expanded, setExpanded] = useState(false);
  const [videoIndex, setVideoIndex] = useState(0);
  const inlineRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const expandedRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const handleEnded = () => {
    setVideoIndex(prev => (prev + 1) % PLAYLIST.length);
  };
  useEffect(() => {
    // Play active video
    inlineRefs.current[videoIndex]?.play().catch(() => {});
    expandedRefs.current[videoIndex]?.play().catch(() => {});
    // Pause inactive video after fade transition
    const inactiveIndex = (videoIndex + 1) % PLAYLIST.length;
    const timeout = setTimeout(() => {
      const inlineInactive = inlineRefs.current[inactiveIndex];
      if (inlineInactive) {
        inlineInactive.pause();
        inlineInactive.currentTime = 0;
      }
      const expandedInactive = expandedRefs.current[inactiveIndex];
      if (expandedInactive) {
        expandedInactive.pause();
        expandedInactive.currentTime = 0;
      }
    }, 1000);
    return () => clearTimeout(timeout);
  }, [videoIndex]);
  // Autoplay first video on mount
  useEffect(() => {
    inlineRefs.current[0]?.play().catch(() => {});
  }, []);
  return <>
      <motion.div initial={{
      opacity: 0,
      scale: 0.97
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={base ?? {
      duration: 0.9,
      delay: 0.4
    }} className="flex items-center justify-center min-h-0 group relative" data-id="element-598">
        <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-black/5" style={{
        background: 'rgba(255,255,255,0.5)',
        border: '1px solid rgba(26,31,43,0.08)',
        boxShadow: '0 12px 40px -12px rgba(14,93,85,0.12)'
      }} data-id="element-599">
          {PLAYLIST.map((src, i) => <video key={src} ref={el => inlineRefs.current[i] = el} src={src} className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-1000 ${i === videoIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'}`} muted playsInline preload="auto" onEnded={i === videoIndex ? handleEnded : undefined} style={{
          borderRadius: 'inherit'
        }} data-id="element-600" />)}
          {/* Expand button on hover */}
          <button onClick={() => setExpanded(true)} className="absolute top-3 left-3 w-9 h-9 rounded-lg bg-white/90 backdrop-blur shadow-md flex items-center justify-center text-[#0B1328] opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white z-20" aria-label="Expand video" data-id="element-601">
            <MaximizeIcon className="w-4 h-4" data-id="element-602" />
          </button>
        </div>
      </motion.div>

      {/* Expanded Overlay */}
      <AnimatePresence data-id="element-603">
        {expanded && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-8" onClick={() => setExpanded(false)} data-id="element-604">
            <motion.div initial={{
          scale: 0.85,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.85,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: [0.22, 1, 0.36, 1]
        }} className="relative w-full max-w-6xl aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black" onClick={e => e.stopPropagation()} data-id="element-605">
              {PLAYLIST.map((src, i) => <video key={src} ref={el => expandedRefs.current[i] = el} src={src} className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-1000 ${i === videoIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'}`} muted playsInline preload="auto" onEnded={i === videoIndex ? handleEnded : undefined} data-id="element-606" />)}
              <button onClick={() => setExpanded(false)} className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur shadow-lg flex items-center justify-center text-[#0B1328] hover:bg-white transition-colors z-20" aria-label="Minimize video" data-id="element-607">
                <MinimizeIcon className="w-5 h-5" data-id="element-608" />
              </button>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}